import React, { useState } from 'react';
import ReusableIcon from './ReusableIcon';
import { TableOfContents, TableOfContentsItem } from "interface/ReportContent";
import styles from 'styles/components/TableOfContentItem.module.sass'

/**
 * The sub-topics of a section, in order. Newer responses carry them as `items`; an older response (or a section
 * read from the old table) only has `description`, one sub-topic per line.
 */
export const subTopicsOf = (toc: TableOfContents): TableOfContentsItem[] => {
    if (toc.items && toc.items.length > 0) return toc.items;
    return (toc.description || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line !== '')
        .map((line) => ({ title: line, page: -1 }));
}

/**
 * TableOfContentItem component renders a single item in the table of contents.
 * It displays the title and description, and allows toggling the visibility of the content.
 *
 * @param {Object} props - The properties for the component.
 * @param {TableOfContents} props.toc - The table of contents item data.
 * 
 * @returns {JSX.Element} The rendered TableOfContentItem component.
 */
const TableOfContentItem: React.FC<{ toc: TableOfContents }> = ({ toc }) => {
    const [isOpen, setIsOpen] = useState(false);
    const subTopics = subTopicsOf(toc);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    }

    return (
        <div className={styles['toc-item']} key={toc.title}>
            <div className={styles['toc-heading-wrapper']} onClick={toggleOpen}>
                <div className={styles['toc-left']}>
                    <ReusableIcon icon={isOpen ? 'tabler:chevron-up' : 'tabler:chevron-down'} className={styles['toc-icon'] || 'toc-icon'} color="#111111" />
                    <span className={styles['toc-item-heading']}>{toc.title}</span>
                </div>
                <div className={styles['toc-right']}>
                    {toc.page > 0 && (
                        <div className={styles['toc-page-icon-wrapper']}>
                            <p className={styles['toc-item-page']}>{toc.page > 0 ? toc.page : ''}</p>
                        </div>
                    )}
                </div>
            </div>
            {isOpen && (
                <div className={styles['toc-content']}>
                    {subTopics.map((item, i) => (
                        <div key={i} className={styles['toc-item-subheading-wrapper']}>
                            <ReusableIcon icon={'hugeicons:google-doc'} className={styles['content-icon'] || 'content-icon'} color="#005581" />
                            <span className={styles['toc-item-subheading']}>{item.title}</span>
                            {item.page > 0 && <span className={styles['toc-item-subpage']}>{item.page}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default TableOfContentItem;
